/* eslint-disable react/prop-types */
import { useState } from "react";
import axios from "@/api/axios";
import { useSession } from "@/hooks/useSession";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  Box,
  Avatar,
  Button,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CardMedia,
} from "@mui/material";
import {
  ArrowUpward,
  ArrowDownward,
  Sort,
  DeleteForever,
  BadgeOutlined,
  Close,
  PictureAsPdf,
  Fullscreen,
} from "@mui/icons-material";
import { styled, useTheme, alpha } from "@mui/material/styles";
import { convertUTCToLocal } from "@/lib/utils/dateUtils";
import NewDeleteModal from "@/components/newDeleteModal";
import { useTranslation } from "react-i18next";

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[1],
  overflow: "hidden",
  "& .MuiTable-root": {
    borderCollapse: "separate",
  },
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.6)} 100%)`,
  "& .MuiTableCell-head": {
    fontWeight: 700,
    fontSize: "0.8125rem",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    borderBottom: `2px solid ${theme.palette.divider}`,
    padding: theme.spacing(2),
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: "all 0.2s ease",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    transform: "translateY(-1px)",
    boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.1)}`,
  },
  "& .MuiTableCell-root": {
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
    padding: theme.spacing(2),
    fontSize: "0.8125rem",
  },
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: theme.spacing(3),
    boxShadow: theme.shadows[24],
    border: "1px solid",
    borderColor: theme.palette.divider,
    background: theme.palette.background.paper,
    overflow: "hidden",
    maxHeight: "90vh",
    maxWidth: "600px",
    width: "90vw",
  },
}));

const CertificatesTable = ({
  userName,
  certificates,
  userId,
  handleApiError,
  setToast,
  onCertificateDelete,
  isOwnProfile = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { token, session } = useSession();

  // State for sorting
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  // State for certificate details dialog
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  // State for delete confirmation
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [certificateToDelete, setCertificateToDelete] = useState(null);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key && prev.direction === "asc") {
        return { key, direction: "desc" };
      }
      return { key, direction: "asc" };
    });
  };

  const sortCertificates = (certs) => {
    if (!sortConfig.key || !certs) return certs;

    const sorted = [...certs];
    const { key, direction } = sortConfig;

    sorted.sort((a, b) => {
      let aValue, bValue;

      switch (key) {
        case "title":
          aValue = a.title || "";
          bValue = b.title || "";
          break;
        case "created_at":
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          break;
        case "expires":
          aValue = a.expires ? 1 : 0;
          bValue = b.expires ? 1 : 0;
          break;
        case "expires_at":
          aValue = a.expires_at ? new Date(a.expires_at).getTime() : 0;
          bValue = b.expires_at ? new Date(b.expires_at).getTime() : 0;
          break;
        case "status":
          aValue = getStatusPriority(getStatus(a?.expires_at, a?.expires));
          bValue = getStatusPriority(getStatus(b?.expires_at, b?.expires));
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  const getStatus = (expires_at, expires) => {
    if (!expires || !expires_at) return t("certificatesTable.statusActive");
    const expirationDate = new Date(expires_at);
    const today = new Date();
    const daysUntilExpiration = Math.ceil(
      (expirationDate - today) / (1000 * 60 * 60 * 24)
    );

    if (daysUntilExpiration < 0) {
      return t("certificatesTable.statusExpired");
    }

    if (daysUntilExpiration <= 90) {
      return t("certificatesTable.statusWarning", {
        days: daysUntilExpiration,
      });
    }

    return t("certificatesTable.statusActive");
  };

  const getExpiryDate = (expires_at, expires) => {
    if (!expires || !expires_at) {
      return t("certificatesTable.neverExpires");
    }
    return convertUTCToLocal(expires_at);
  };

  const getStatusPriority = (status) => {
    if (status.toLowerCase().includes("expired")) return 3;
    if (status.toLowerCase().includes("warning")) return 2;
    return 1;
  };

  const getStatusStyling = (expires_at, expires, theme) => {
    if (!expires || !expires_at) {
      // Active status (no expiry)
      return {
        backgroundColor: alpha(theme.palette.success.main, 0.15),
        color: theme.palette.success.dark,
        border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
      };
    }

    const expirationDate = new Date(expires_at);
    const today = new Date();
    const daysUntilExpiration = Math.ceil(
      (expirationDate - today) / (1000 * 60 * 60 * 24)
    );

    if (daysUntilExpiration < 0) {
      // Expired
      return {
        backgroundColor: alpha(theme.palette.error.main, 0.15),
        color: theme.palette.error.dark,
        border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
      };
    }

    if (daysUntilExpiration <= 90) {
      // Warning (expires within 90 days)
      return {
        backgroundColor: alpha(theme.palette.warning.main, 0.15),
        color: theme.palette.warning.dark,
        border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
      };
    }

    // Active status
    return {
      backgroundColor: alpha(theme.palette.success.main, 0.15),
      color: theme.palette.success.dark,
      border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
    };
  };

  const handleRowClick = (certificate) => {
    setSelectedCertificate(certificate);
    setDetailsDialogOpen(true);
  };

  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedCertificate(null);
  };

  const handleDeleteClick = (e, certificate) => {
    e.stopPropagation(); // Prevent row click
    setCertificateToDelete(certificate);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!certificateToDelete) return;

    try {
      await axios.delete(
        `/api/v1/users/${userId}/certificates/${certificateToDelete.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setToast({
        open: true,
        severity: "success",
        message: t("certificatesTable.deleteSuccessToast"),
      });
      onCertificateDelete();

      // Close details dialog if this certificate was being viewed
      if (selectedCertificate?.id === certificateToDelete.id) {
        handleCloseDetailsDialog();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setDeleteModalOpen(false);
      setCertificateToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setCertificateToDelete(null);
  };

  const canDeleteCertificate = () => {
    // User can delete their own certificates, or admin can delete any certificate
    return isOwnProfile || session.user_type === "ORG_ADMIN";
  };

  const renderSortButton = (field, label) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {label}
      <IconButton
        size="small"
        onClick={() => handleSort(field)}
        sx={{
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          "&:hover": {
            bgcolor: alpha(theme.palette.primary.main, 0.12),
            transform: "scale(1.1)",
          },
          transition: "all 0.2s ease",
          width: 24,
          height: 24,
        }}
      >
        {sortConfig.key === field ? (
          sortConfig.direction === "asc" ? (
            <ArrowUpward sx={{ fontSize: 14, color: "primary.main" }} />
          ) : (
            <ArrowDownward sx={{ fontSize: 14, color: "primary.main" }} />
          )
        ) : (
          <Sort sx={{ fontSize: 14, color: "text.secondary" }} />
        )}
      </IconButton>
    </Box>
  );

  if (!certificates) {
    return (
      <StyledTableContainer component={Paper}>
        <Table>
          <StyledTableHead>
            <TableRow>
              {[
                "Certificate",
                "Issue Date",
                "Expires",
                "Expiry Date",
                "Status",
                "Actions",
              ].map((header, index) => (
                <TableCell key={index}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Skeleton variant="text" width={100} height={20} />
                    <Skeleton variant="circular" width={16} height={16} />
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {[...Array(3)].map((_, index) => (
              <TableRow key={index}>
                <TableCell>
                  <Skeleton variant="text" width="80%" height={20} />
                </TableCell>
                <TableCell>
                  <Skeleton variant="text" width="60%" height={20} />
                </TableCell>
                <TableCell>
                  <Skeleton variant="text" width="40%" height={20} />
                </TableCell>
                <TableCell>
                  <Skeleton variant="text" width="70%" height={20} />
                </TableCell>
                <TableCell>
                  <Skeleton
                    variant="rectangular"
                    width={60}
                    height={24}
                    sx={{ borderRadius: 1 }}
                  />
                </TableCell>
                <TableCell>
                  <Skeleton variant="circular" width={32} height={32} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </StyledTableContainer>
    );
  }

  if (Array.isArray(certificates) && certificates.length === 0) {
    return (
      <Paper
        sx={{
          p: 6,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          textAlign: "center",
          background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.7)} 0%, ${alpha(theme.palette.background.default, 0.4)} 100%)`,
        }}
      >
        <BadgeOutlined
          sx={{
            fontSize: 64,
            color: "text.disabled",
            mb: 2,
          }}
        />
        <Typography
          variant="h6"
          sx={{
            color: "text.secondary",
            fontWeight: 600,
            mb: 1,
          }}
        >
          {t("certificatesTable.NocertificateFound")}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "text.disabled",
          }}
        >
          {t("certificatesTable.noCertificatesText", { userName })}
        </Typography>
      </Paper>
    );
  }

  const sortedCertificates = sortCertificates(certificates);

  return (
    <>
      {/* Certificates Table */}
      <StyledTableContainer component={Paper}>
        <Table aria-label={t("certificatesTable.tableAria")}>
          <StyledTableHead>
            <TableRow>
              <TableCell>
                {renderSortButton(
                  "title",
                  t("certificatesTable.certificateHeader")
                )}
              </TableCell>
              <TableCell>
                {renderSortButton(
                  "created_at",
                  t("certificatesTable.issueDateHeader")
                )}
              </TableCell>
              <TableCell>
                {renderSortButton(
                  "expires",
                  t("certificatesTable.expiresHeader")
                )}
              </TableCell>
              <TableCell>
                {renderSortButton(
                  "expires_at",
                  t("certificatesTable.expiryDateHeader")
                )}
              </TableCell>
              <TableCell>
                {renderSortButton(
                  "status",
                  t("certificatesTable.statusHeader")
                )}
              </TableCell>
              <TableCell align="center">
                {t("certificatesTable.actionHeader")}
              </TableCell>
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {sortedCertificates?.map((cert) => {
              const status = getStatus(cert?.expires_at, cert?.expires);
              const expiryDate = getExpiryDate(cert?.expires_at, cert?.expires);

              return (
                <StyledTableRow
                  key={cert.id}
                  onClick={() => handleRowClick(cert)}
                  aria-label={t("certificatesTable.rowClickAria", {
                    title: cert.title,
                  })}
                >
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: "primary.main",
                        }}
                      >
                        <BadgeOutlined sx={{ fontSize: 18 }} />
                      </Avatar>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500, color: "text.primary" }}
                      >
                        {cert.title}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        fontFamily: "monospace",
                        fontSize: "0.75rem",
                      }}
                    >
                      {convertUTCToLocal(cert?.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: cert?.expires ? "warning.main" : "success.main",
                      }}
                    >
                      {cert?.expires
                        ? t("certificatesTable.yesText")
                        : t("certificatesTable.noText")}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        fontFamily: "monospace",
                        fontSize: "0.75rem",
                      }}
                    >
                      {expiryDate}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={status}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        height: 24,
                        ...getStatusStyling(
                          cert?.expires_at,
                          cert?.expires,
                          theme
                        ),
                      }}
                      aria-label={t("certificatesTable.statusChipAria", {
                        status,
                      })}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {canDeleteCertificate() && (
                      <Tooltip title={t("certificatesTable.deleteTooltip")}>
                        <IconButton
                          onClick={(e) => handleDeleteClick(e, cert)}
                          size="small"
                          sx={{
                            bgcolor: alpha(theme.palette.error.main, 0.08),
                            "&:hover": {
                              bgcolor: alpha(theme.palette.error.main, 0.12),
                              transform: "scale(1.1)",
                            },
                            transition: "all 0.2s ease",
                          }}
                          aria-label={t("certificatesTable.deleteAria", {
                            title: cert.title,
                          })}
                        >
                          <DeleteForever
                            sx={{ fontSize: 18, color: "error.main" }}
                          />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
      </StyledTableContainer>

      {/* Simple Certificate Details Dialog */}
      <StyledDialog
        open={detailsDialogOpen}
        onClose={handleCloseDetailsDialog}
        aria-labelledby="certificate-details-dialog-title"
      >
        {selectedCertificate && (
          <>
            <DialogTitle
              id="certificate-details-dialog-title"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                py: 3,
                px: 4,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                }}
              >
                {selectedCertificate.title}
              </Typography>
              <IconButton
                onClick={handleCloseDetailsDialog}
                sx={{
                  bgcolor: alpha(theme.palette.text.primary, 0.08),
                  "&:hover": {
                    bgcolor: alpha(theme.palette.text.primary, 0.12),
                  },
                }}
                aria-label="Close certificate details"
              >
                <Close sx={{ fontSize: 20 }} />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
              {/* Certificate Attachment/Picture */}
              {selectedCertificate.attachments?.[0] && (
                <Box
                  sx={{
                    textAlign: "center",
                    p: 4,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  {(() => {
                    const attachment = selectedCertificate.attachments[0];
                    const isPDF = attachment?.url
                      ?.toLowerCase()
                      .endsWith(".pdf");

                    if (isPDF) {
                      return (
                        <Box>
                          <PictureAsPdf
                            sx={{ fontSize: 80, color: "error.main", mb: 2 }}
                          />
                          <Typography
                            variant="h6"
                            sx={{ mb: 2, fontWeight: 600 }}
                          >
                            PDF Document
                          </Typography>
                          <Button
                            variant="contained"
                            onClick={() =>
                              window.open(attachment.url, "_blank")
                            }
                            startIcon={<Fullscreen />}
                            sx={{
                              borderRadius: 2,
                              px: 3,
                              py: 1,
                              textTransform: "none",
                              fontWeight: 600,
                            }}
                          >
                            Open PDF
                          </Button>
                        </Box>
                      );
                    } else {
                      return (
                        <Box
                          sx={{ cursor: "pointer" }}
                          onClick={() => window.open(attachment.url, "_blank")}
                        >
                          <CardMedia
                            component="img"
                            image={attachment.url}
                            alt={attachment.display_name}
                            sx={{
                              maxWidth: "100%",
                              maxHeight: "400px",
                              objectFit: "contain",
                              borderRadius: 2,
                              boxShadow: theme.shadows[4],
                              mx: "auto",
                              display: "block",
                              transition: "transform 0.2s ease",
                              "&:hover": {
                                transform: "scale(1.02)",
                              },
                            }}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 1, display: "block" }}
                          >
                            Click to view full size
                          </Typography>
                        </Box>
                      );
                    }
                  })()}
                </Box>
              )}

              {/* Certificate Description */}
              <Box sx={{ p: 4 }}>
                {selectedCertificate.description ? (
                  <>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                        color: "text.primary",
                      }}
                    >
                      Description
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        lineHeight: 1.7,
                        color: "text.secondary",
                      }}
                    >
                      {selectedCertificate.description}
                    </Typography>
                  </>
                ) : (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "text.disabled",
                        fontStyle: "italic",
                      }}
                    >
                      No description available for this certificate.
                    </Typography>
                  </Box>
                )}
              </Box>
            </DialogContent>

            <DialogActions
              sx={{
                p: 3,
                borderTop: "1px solid",
                borderColor: "divider",
                justifyContent: "flex-end",
              }}
            >
              <Button
                onClick={handleCloseDetailsDialog}
                variant="contained"
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </StyledDialog>

      {/* Delete Confirmation Modal */}
      <NewDeleteModal
        open={deleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        itemType={certificateToDelete?.title || "certificate"}
      />
    </>
  );
};

export default CertificatesTable;
